// ============================================================
// หน้า Report — รายงานสรุปผล (รวมทุกรายงานของระบบไว้หน้าเดียว)
// ------------------------------------------------------------
// โครงของไฟล์นี้ (ไล่จากบนลงล่าง)
//   1. ตัวช่วยเล็ก ๆ            norm / m / tf / isSettled / refCauseText
//   2. FLAG_FIELDS, PAY_FIELDS  นิยามช่องค่าใช้จ่ายและฟิลด์สถานะที่ทุกรายงานใช้ร่วมกัน
//   3. REPORT_MENU              รายการรายงานทั้งหมดที่เลือกได้จากเมนูซ้าย
//   4. PRINT_BUTTONS            ปุ่มพิมพ์/ส่งออกของแต่ละรายงาน
//   5. C                        นิยามคอลัมน์ที่ใช้ซ้ำในหลายรายงาน
//   6. REPORTS                  ★ ตัวหลัก — แต่ละรายงานคือ 1 entry บอกว่าเรียก API ไหน
//                                 แปลงข้อมูลยังไง มีคอลัมน์อะไร รวมยอดท้ายตารางยังไง
//   7. class Report             โหลดข้อมูล + วาดตาราง + สั่งพิมพ์ PDF/Excel
//
// เพิ่มรายงานใหม่ = เพิ่ม entry ใน REPORT_MENU + REPORTS (ไม่ต้องแตะ render)
// ============================================================

import "../../shared/config/global.js";
import "../../assets/css/bootstrap.css";
import Navbar from "../../shared/layout/Navbar";
import Select from "react-select";
import {
  tokens,
  select_style,
  GET,
  POST,
  format_date,
  total,
  DatePicker,
  getStorage,
  permission,
  ScrollTopButton,
  fieldSorter,
  loading,
  alert,
  toFixed,
  roundTo,
  isWaived,
  BILL_FIELDS,
  sumBillFields,
} from "../../shared/index.js";
import {
  report1,
  report2,
  report3,
  report4,
  report5,
  report6,
  report7,
  report8,
  report9,
  report10,
  report11,
  report12,
  report13,
} from "../../documents/builders/Report.js";
import React, { Component } from "react";
import { DownloadTableExcel } from "react-export-table-to-excel";

const perm = permission("all-report");

/* ----------------------------------------------------------------------------
 * Helpers
 * --------------------------------------------------------------------------*/
const norm = (v) => Number(toFixed(v).replaceAll(",", ""));
// money cell renderer ( value ? formatted : "0.00" )
const m = (v) => (v ? toFixed(v) : "0.00");
// footer total renderer
const tf = (data, f) => toFixed(total(data, f));

// report_clear_bill ส่ง record_water_list มา 2 รูปแบบ: object เดี่ยว (ไม่มีใบเสร็จ/ตัดหนี้แบบเลือกเฉพาะรายการ)
// หรือ array (มีใบเสร็จผูกหลายบิล) — ฟังก์ชันนี้ normalize ให้เป็นรูปแบบเดียวกันก่อนใช้งาน กัน error/ตกหล่นข้อมูล
const normalizeRwList = (rwList) => {
  if (!rwList) return { statusPay: null, cutOffDebt: null };
  const arr = Array.isArray(rwList) ? rwList : [rwList];
  const statusPay = arr.length && arr.every((r) => r?.status_pay === 3) ? 3 : arr[0]?.status_pay ?? null;
  return { statusPay, cutOffDebt: arr[0]?.cut_off_debt || null };
};

// 1 ใบเสร็จอาจอ้างถึงหลายบิลน้ำ (record_water_list) — รายงานใบเสร็จรับเงินต้องแสดงเลขมาตร/วันที่จดมาตร
// ของ "บิลล่าสุด" ใบเดียว (ตัดสินจาก datetime_create มากสุด, เท่ากันให้ใช้ตัวท้าย list)
// ไม่ใช้ i.current_gauge_number/last_gauge_number/unit_used ตรง ๆ เพราะฟิลด์เหล่านั้นไม่มีอยู่ระดับใบเสร็จ
const latestRecordWater = (rwList) => {
  if (!rwList || !rwList.length) return null;
  return rwList.reduce((latest, cur) => {
    if (!latest) return cur;
    const lt = new Date(latest.datetime_create).getTime();
    const ct = new Date(cur.datetime_create).getTime();
    return ct >= lt ? cur : latest;
  }, null);
};

// (s_flag, field, sign) — เมื่อ s_flag === false ให้บวก(ตาม sign) ไม่งั้น set field = 0
const FLAG_FIELDS = [
  ["s_add_debt", "add_debt", 1],
  ["s_cable_cost", "cable_cost", 1],
  ["s_common_expenses", "common_expenses", 1],
  ["s_equipment_maintenance_fee", "equipment_maintenance_fee", 1],
  ["s_fire_cost", "fire_cost", 1],
  ["s_late_payment", "late_payment", 1],
  ["s_meter_connect_cost", "meter_connect_cost", 1],
  ["s_other_income", "other_income", 1],
  ["s_reduce_debt", "reduce_debt", -1],
  ["s_waste_cost", "waste_cost", 1],
  ["s_water_bill", "water_bill", 1],
];

// ช่องนี้จบแล้วหรือยัง (เก็บเงินแล้ว/ตัดหนี้แล้ว)
// NOTE s_* ระดับงวดเป็นสถานะรวมของใบ เชื่อรายช่องไม่ได้ — backend ตั้งเป็น true ให้อัตโนมัติเมื่อ cost ของช่องนั้น
// เป็น 0 ตอนออกใบเสร็จ (record_receipt/views.py "True if c_xxx == 0 or s_xxx == True") ทั้งที่ยังไม่ได้เก็บเงินจริง
// ให้ยึด transaction_payments/writeoff_list เป็นหลักเสมอ ไม่ fallback ไป item[flag]
// เพราะหลังยกเลิกการตัดหนี้/ยกเลิกใบเสร็จ backend ไม่ได้ล้าง s_* ระดับงวด ยอดที่กลับมาค้างจะหายจากรายงาน
// (รายงานกลุ่มนี้ดึงจาก report/overdue ซึ่งคืนเฉพาะงวด status_pay = 1 อยู่แล้ว)
const isSettled = (item, flag) => isWaived(item, flag);

// normalize + คำนวณ total_amount ตาม s_flag (ใช้กับ report 1,2,4,6)
function settleFlags(item, extra = []) {
  for (const f of [
    "late_payment",
    "common_expenses",
    "cable_cost",
    "fire_cost",
    ...extra,
  ]) {
    item[f] = norm(item[f]);
  }
  let total_cost = 0;
  for (const [flag, field, sign] of FLAG_FIELDS) {
    const settled = isSettled(item, flag);
    item[flag] = settled; // sync ให้จุดที่อ่าน s_* ต่อ (เช่น การรวมยอดหลายงวด) ใช้ค่าเดียวกัน
    if (!settled) total_cost += sign * item[field];
    else item[field] = 0;
  }
  item.total_amount = norm(total_cost);
  return item;
}

// (s_flag, field) สำหรับ transaction_payments (ใช้กับ report 5,8)
const PAY_FIELDS = [
  ["s_water_bill", "water_bill"],
  ["s_waste_cost", "waste_cost"],
  ["s_reduce_debt", "reduce_debt"],
  ["s_other_income", "other_income"],
  ["s_meter_connect_cost", "meter_connect_cost"],
  ["s_late_payment", "late_payment"],
  ["s_fire_cost", "fire_cost"],
  ["s_equipment_maintenance_fee", "equipment_maintenance_fee"],
  ["s_common_expenses", "common_expenses"],
  ["s_cable_cost", "cable_cost"],
  ["s_add_debt", "add_debt"],
];

// รวมยอดจาก transaction_payments + add_on debt แล้วคิด total (ใช้กับ report 5,8)
function processPayments(item, addOther) {
  if (item.transaction_payments?.length > 0) {
    item.transaction_payments = item.transaction_payments.filter(
      (e) => e.ref_iw_number === item.iw_number,
    );
  }
  for (const [flag, field] of PAY_FIELDS) {
    const paid = item.transaction_payments?.filter((p) => p[flag]) ?? [];
    if (paid.length > 0) item[field] = total(paid, field);
    // ไม่มี transaction ยืนยันรายการนี้ -> เช็ค s_flag ของใบเสร็จเอง (true = รายการนี้เก็บเงินในใบเสร็จนี้ ให้นับ, false = ไม่ได้เก็บในใบเสร็จนี้ zero ทิ้ง)
    else if (!item[flag]) item[field] = 0;
  }
  if (addOther) {
    for (const o of item.record_other_list) item.other_income += o.cost;
  }
  item.reduce_debt = item.reduce_debt + item.add_on_reduce_debt;
  item.add_debt = item.add_debt + item.add_on_add_debt;
  const t =
    item.water_bill +
    item.waste_cost +
    item.other_income +
    item.meter_connect_cost +
    (item.late_payment || 0) +
    item.fire_cost +
    item.equipment_maintenance_fee +
    item.common_expenses +
    item.cable_cost +
    item.add_debt -
    item.reduce_debt;
  item.total_amount = t;
  item.total_final = t;
}

const refCauseText = (rc) =>
  rc === 1
    ? "ลูกค้าชำระแล้ว"
    : rc === 2
    ? "ลูกค้าเปลี่ยนสัญญา (บรรจุใหม่) ห้อง กคช."
    : "ลูกค้าบรรจุใหม่ ซื้อขายโอนกรรมสิทธิ์";

/* ----------------------------------------------------------------------------
 * รายการรายงาน (เมนูด้านซ้าย) + ปุ่มพิมพ์
 * --------------------------------------------------------------------------*/
const REPORT_MENU = [
  { type: "รายงานหนี้ค้างชำระ" },
  { type: "รายงานสถานะห้องชุด" },
  { type: "รายงานแจ้งหนี้ประจำเดือน" },
  { type: "รายงานแจ้งหนี้คงค้างย้อนหลัง" },
  { type: "รายงานใบเสร็จรับเงิน" },
  { type: "รายงานลูกหนี้" },
  { type: "รายงานอื่น ๆ ประจำเดือน" },
  { type: "รายงานสรุปการจัดเก็บเงิน" },
  { type: "รายงานสรุปรวมยกเลิกใบเสร็จ" },
  { type: "รายงานแสดงรายละเอียดยกเลิกใบเสร็จ" },
  { type: "รายงานการตัดหนี้", extra: { ref_cause: "1" } },
  { type: "รายงานลูกหนี้-โครงการ", extra: { ref_cause: "2" } },
  { type: "รายงานลูกหนี้คงค้าง ณ เดือน" },
  { type: "ส่งออกข้อมูลระบบ" },
];

// ปุ่มพิมพ์ท้ายแถบตัวกรอง (ยกเว้น รายงานสถานะห้องชุด ที่อยู่กลุ่ม ml-auto)
const PRINT_BUTTONS = {
  รายงานหนี้ค้างชำระ: { print: "พิมพ์รายงานหนี้ค้างชำระ", label: "พิมพ์รายงานหนี้ค้างชำระ" },
  รายงานแจ้งหนี้ประจำเดือน: { print: "รายงานแจ้งหนี้ประจำเดือน", label: "รายงานแจ้งหนี้ประจำเดือน" },
  รายงานแจ้งหนี้คงค้างย้อนหลัง: {
    print: "พิมพ์รายงานแจ้งหนี้คงค้างย้อนหลัง",
    label: "พิมพ์รายงานแจ้งหนี้คงค้างย้อนหลัง",
  },
  รายงานใบเสร็จรับเงิน: { print: "พิมพ์ใบเสร็จรับเงินประจำเดือน", label: "พิมพ์ใบเสร็จรับเงินประจำเดือน" },
  รายงานลูกหนี้: { print: "พิมพ์รายงานลูกหนี้", label: "พิมพ์รายงานลูกหนี้" },
  "รายงานอื่น ๆ ประจำเดือน": { print: "พิมพ์รายงานอื่น ๆ ประจำเดือน", label: "พิมพ์" },
  รายงานสรุปการจัดเก็บเงิน: { print: "พิมพ์รายงานสรุปการจัดเก็บเงิน", label: "พิมพ์" },
  รายงานสรุปรวมยกเลิกใบเสร็จ: { print: "พิมพ์รายงานสรุปรวมยกเลิกใบเสร็จ", label: "พิมพ์" },
  รายงานแสดงรายละเอียดยกเลิกใบเสร็จ: {
    print: "พิมพ์รายงานแสดงรายละเอียดยกเลิกใบเสร็จ",
    label: "พิมพ์",
  },
  รายงานการตัดหนี้: { print: "แสดงสรุป", label: "แสดงสรุป" },
  "รายงานลูกหนี้-โครงการ": { print: "แสดงสรุป12", label: "แสดงสรุป" },
  "รายงานลูกหนี้คงค้าง ณ เดือน": { print: "พิมพ์รายงานลูกหนี้คงค้าง ณ เดือน", label: "แสดงสรุป" },
};

/* ----------------------------------------------------------------------------
 * นิยามตาราง (header + cell + footer) ของแต่ละรายงาน
 *  col = [headerText, (item, index, data) => cellContent, nowrap?]
 *  foot = (data, totals) => [cell, cell, ...]  (อยู่แนวเดียวกับ cols)
 * --------------------------------------------------------------------------*/
const C = {
  addrZW: ["บ้านเลขที่", (i) => (i.address_number ? i.address_number + "\u200B" : "-")],
  addrSp: ["บ้านเลขที่", (i) => (i.address_number ? "ㅤ" + i.address_number : "-")],
  name: ["ชื่อ - นามสกุล", (i) => i.full_name || "-", true],
  recordDate: ["วันที่จดมาตร", (i) => (i.datetime_create ? format_date(i.datetime_create, "dd/mm/yyyy") : "-")],
  unit: ["จำนวนหน่วยใช้", (i) => i.unit_used || "0"],
  water: ["ค่าน้ำประปา", (i) => m(i.water_bill)],
  equip: ["ค่ารักษาอุปกรณ์น้ำ", (i) => m(i.equipment_maintenance_fee)],
  common: ["ค่าส่วนกลาง", (i) => m(i.common_expenses)],
  waste: ["ค่าขยะ", (i) => m(i.waste_cost)],
  cable: ["ค่าเคเบิล", (i) => m(i.cable_cost)],
  otherMeter: [
    "รายรับอื่นๆ",
    (i) => (i.other_income || i.meter_connect_cost ? toFixed(i.other_income + i.meter_connect_cost) : "0.00"),
  ],
  fire: ["ค่าเบี้ยประกัน", (i) => m(i.fire_cost)],
  addReduce: [
    "เพิ่ม/ลดหนี้",
    (i) => (i.add_debt || i.reduce_debt ? toFixed(i.add_debt - i.reduce_debt) : "0.00"),
  ],
  late: ["ค่าปรับล่าช้า", (i) => m(i.late_payment)],
  sum: ["รวม (บาท)", (i) => m(i.total_amount)],
  payStatus: ["สถานะ", (i) => i.pay_status_text || "-", true],
  gaugeCur: ["เลขมาตร", (i) => i.current_gauge_number || "0"],
  gaugeLast: ["เลขมาตร", (i) => i.last_gauge_number || "0"],
  addRemark: ["เพิ่มหนี้", (i) => i.add_on_add_debt_remark || i.remark_add_debt || ""],
  reduceRemark: ["ลดหนี้", (i) => i.add_on_reduce_debt_remark || i.remark_reduce_debt || ""],
};

const REPORTS = {
  รายงานหนี้ค้างชำระ: {
    span: 14,
    cols: [C.addrZW, C.name, C.unit, C.water, C.equip, C.common, C.waste, C.cable, C.otherMeter, C.fire, C.addReduce, C.late, C.sum],
    foot: (d) => [
      "รวมทั้งสิ้น", "", "",
      tf(d, "water_bill"), tf(d, "equipment_maintenance_fee"), tf(d, "common_expenses"),
      tf(d, "waste_cost"), tf(d, "cable_cost"),
      toFixed(total(d, "other_income") + total(d, "meter_connect_cost")),
      tf(d, "fire_cost"),
      toFixed(total(d, "add_debt") - total(d, "reduce_debt")),
      tf(d, "late_payment"), tf(d, "total_amount"),
    ],
  },
  รายงานสถานะห้องชุด: {
    span: 16,
    cols: [
      C.addrZW, C.name, C.recordDate, C.gaugeCur, C.gaugeLast, C.unit, C.water, C.equip, C.common,
      C.waste, C.cable, C.otherMeter, C.fire,
      C.addReduce,
      C.late, C.sum, C.addRemark, C.reduceRemark,
    ],
    foot: (d) => [
      "รวมทั้งสิ้น", "", "", "", "",
      total(d, "unit_used"),
      tf(d, "water_bill"), tf(d, "equipment_maintenance_fee"), tf(d, "common_expenses"),
      tf(d, "waste_cost"), tf(d, "cable_cost"),
      toFixed(total(d, "other_income") + total(d, "meter_connect_cost")),
      tf(d, "fire_cost"),
      toFixed(total(d, "add_debt") - total(d, "reduce_debt")),
      tf(d, "late_payment"), tf(d, "total_amount"), "", "",
    ],
  },
  รายงานแจ้งหนี้ประจำเดือน: {
    span: 15,
    markDup: true,
    cols: [
      C.addrSp, C.name,
      ["วันที่จดมาตร", C.recordDate[1]],
      ["เลขมาตรจดครั้งนี้", C.gaugeCur[1]],
      ["เลขมาตรจดครั้งก่อน", C.gaugeLast[1]],
      C.unit, C.water, C.equip, C.common, C.waste, C.cable, C.otherMeter, C.fire,
      C.addReduce,
      C.late, C.sum, C.payStatus,
    ],
    foot: (d) => [
      "รวมทั้งสิ้น", "", "", "", "", "",
      tf(d, "water_bill"), tf(d, "equipment_maintenance_fee"), tf(d, "common_expenses"),
      tf(d, "waste_cost"), tf(d, "cable_cost"),
      toFixed(total(d, "other_income") + total(d, "meter_connect_cost")),
      tf(d, "fire_cost"),
      toFixed(total(d, "add_debt") - total(d, "reduce_debt")),
      tf(d, "late_payment"), tf(d, "total_amount"), "",
    ],
  },
  รายงานแจ้งหนี้คงค้างย้อนหลัง: {
    span: 14,
    cols: [
      C.addrSp, C.name, C.recordDate, C.gaugeCur, C.gaugeLast, C.unit, C.water, C.equip, C.common,
      C.waste, C.cable, C.otherMeter, C.fire, C.addReduce, C.late,
      [
        "รวม (บาท)",
        (i) =>
          toFixed(
            i.water_bill + i.equipment_maintenance_fee + i.common_expenses + i.waste_cost +
              i.cable_cost + i.other_income + i.meter_connect_cost + i.fire_cost + i.add_debt +
              i.late_payment - i.reduce_debt,
          ),
      ],
      C.addRemark, C.reduceRemark,
    ],
    foot: (d) => [
      "รวมทั้งสิ้น", "", "", "", "",
      total(d, "unit_used"),
      tf(d, "water_bill"), tf(d, "equipment_maintenance_fee"), tf(d, "common_expenses"),
      tf(d, "waste_cost"), tf(d, "cable_cost"),
      toFixed(total(d, "other_income") + total(d, "meter_connect_cost")),
      tf(d, "fire_cost"),
      toFixed(total(d, "add_debt") - total(d, "reduce_debt")),
      tf(d, "late_payment"),
      toFixed(
        total(d, "water_bill") + total(d, "equipment_maintenance_fee") + total(d, "common_expenses") +
          total(d, "waste_cost") + total(d, "cable_cost") + total(d, "other_income") +
          total(d, "meter_connect_cost") + total(d, "fire_cost") + total(d, "add_debt") +
          total(d, "late_payment") - total(d, "reduce_debt"),
      ),
      "", "",
    ],
  },
  รายงานใบเสร็จรับเงิน: {
    span: 21,
    src: "record_water_list",
    sticky: true,
    cols: [
      ["เลขที่รับใบเสร็จ", (i) => i.iw_number || "-"],
      ["วันที่ใบเสร็จรับเงิน", (i) => (i.record_date ? format_date(i.record_date) : "-")],
      C.addrSp, C.name,
      // ★ วันที่จดมาตร/เลขมาตรของบิลน้ำที่ใบเสร็จนี้อ้างถึง — 1 ใบเสร็จอาจผูกหลายบิล (record_water_list)
      //   ให้ยึดบิลล่าสุด (datetime_create มากสุด) ใบเดียว ไม่ใช่ i.datetime_create ที่เป็นเวลาบันทึกใบเสร็จ
      //   และไม่ใช่ i.current_gauge_number/last_gauge_number/unit_used ตรง ๆ เพราะไม่มีอยู่ระดับใบเสร็จ (ค่าจะว่างเสมอ)
      ["วันที่จดมาตร", (i) => (latestRecordWater(i.record_water_list)?.datetime_create ? format_date(latestRecordWater(i.record_water_list).datetime_create) : "-")],
      ["เลขมาตรจดครั้งนี้", (i) => latestRecordWater(i.record_water_list)?.current_gauge_number || "0"],
      ["เลขมาตรจดครั้งก่อน", (i) => latestRecordWater(i.record_water_list)?.last_gauge_number || "0"],
      ["จำนวนหน่วยใช้", (i) => i.sum_unit_used || "0"],
      ["ค่าน้ำประปา", (i) => toFixed(i.water_bill || 0)],
      ["ค่ารักษาอุปกรณ์น้ำ", (i) => toFixed(i.equipment_maintenance_fee || 0)],
      ["ค่าส่วนกลาง", (i) => toFixed(i.common_expenses || 0)],
      ["ค่าขยะ", (i) => toFixed(i.waste_cost || 0)],
      ["ค่าเคเบิล", (i) => toFixed(i.cable_cost || 0)],
      ["รายรับอื่นๆ", (i) => toFixed(i.other_income || 0)],
      ["ค่าเบี้ยประกัน", (i) => toFixed(i.fire_cost || 0)],
      ["เพิ่ม/ลดหนี้", (i) => toFixed((i.add_debt || 0) - (i.reduce_debt || 0))],
      ["ค่าต่อกลับมิเตอร์", (i) => toFixed(i.meter_connect_cost || 0)],
      ["ค่าปรับชำระล่าช้า", (i) => toFixed(i.late_payment || 0)],
      ["ปรับปรุงยอดเงิน", (i) => toFixed(i.change_satang || 0)],
      [
        "รวม (บาท)",
        (i) =>
          toFixed(
            i.water_bill + i.equipment_maintenance_fee + i.common_expenses + i.waste_cost +
              i.other_income + i.fire_cost + i.cable_cost + (i.add_debt - i.reduce_debt) +
              (i.late_payment || 0) + i.meter_connect_cost + i.change_satang,
          ),
      ],
      ["สาเหตุเพิ่มหนี้", C.addRemark[1]],
      ["สาเหตุลดหนี้", C.reduceRemark[1]],
    ],
    foot: (d) => [
      "รวมทั้งสิ้น", "", "", "", "", "", "", "",
      tf(d, "water_bill"), tf(d, "equipment_maintenance_fee"), tf(d, "common_expenses"),
      tf(d, "waste_cost"), tf(d, "cable_cost"), tf(d, "other_income"), tf(d, "fire_cost"),
      toFixed(total(d, "add_debt") - total(d, "reduce_debt")),
      tf(d, "meter_connect_cost"), tf(d, "late_payment"), tf(d, "change_satang"),
      toFixed(
        total(d, "water_bill") + total(d, "equipment_maintenance_fee") + total(d, "common_expenses") +
          total(d, "waste_cost") + total(d, "cable_cost") + total(d, "other_income") +
          total(d, "fire_cost") + (total(d, "add_debt") - total(d, "reduce_debt")) +
          total(d, "meter_connect_cost") + total(d, "late_payment") + total(d, "change_satang"),
      ),
      "", "",
    ],
  },
  รายงานลูกหนี้: {
    span: 14,
    cols: [
      ["ลำดับ", (i, index) => index + 1],
      C.addrSp, C.name,
      ["เดือนค้างชำระ", (i) => i.count_installment || "0"],
      C.unit, C.water, C.common,
      ["ค่าบริการ", C.equip[1]],
      C.waste, C.cable,
      ["ค่าเบี้ยประกัน", C.fire[1]],
      ["เงินทุน/อื่นๆ", (i) => m(i.other_income)],
      C.addReduce, C.late,
      ["รวมเป็นเงิน (บาท)", (i) => (i.total_amount || i.overdue_amount ? toFixed(i.total_amount + i.overdue_amount) : "0.00")],
    ],
    foot: (d) => [
      "รวมทั้งสิ้น", "", "", "",
      total(d, "unit_used"),
      tf(d, "water_bill"), tf(d, "common_expenses"), tf(d, "equipment_maintenance_fee"),
      tf(d, "waste_cost"), tf(d, "cable_cost"), tf(d, "fire_cost"), tf(d, "other_income"),
      toFixed(total(d, "add_debt") - total(d, "reduce_debt")),
      tf(d, "late_payment"),
      toFixed(total(d, "total_amount") + total(d, "overdue_amount")),
    ],
  },
  "รายงานอื่น ๆ ประจำเดือน": {
    span: 15,
    cols: [
      [
        "บ้านเลขที่",
        (i) => (i.address_number ? "ㅤ" + i.address_number : i.address ? "ㅤ" + i.address : "-"),
      ],
      [
        "รายละเอียด",
        (i) =>
          i.record_other_list[0]
            ? i.record_other_list[0]?.title +
              (i.record_other_list[0]?.description ? " ( " + i.record_other_list[0]?.description + " ) " : "")
            : "-",
      ],
      ["วันที่จดมาตร", (i) => (i.record_date ? format_date(i.record_date) : "-")],
      ["เลขมาตรเดือนนี้", () => "0"],
      ["เลขมาตรเดือนก่อน", () => "0"],
      ["จำนวนหน่วยใช้", () => "0"],
      ["ค่าน้ำประปา", () => "0.00"],
      ["ค่าบริการ", () => "0.00"],
      ["ค่าส่วนกลาง", () => "0.00"],
      ["ค่าขยะ", () => "0.00"],
      ["ค่าเคเบิล", () => "0.00"],
      ["รายรับอื่นๆ", (i) => (i.record_other_list[0] ? toFixed(i.record_other_list[0]?.cost) : "0.00")],
      ["ค่าเบี้ยประกัน", () => "0.00"],
      ["เพิ่ม/ลดหนี้", () => "0.00"],
      ["รวม (บาท)", (i) => (i.total_final ? toFixed(i.total_final) : "0.00")],
    ],
    foot: (d, totals) => [
      "รวมทั้งสิ้น", "", "", "", "", "",
      "0.00", "0.00", "0.00", "0.00", "0.00",
      toFixed(totals), "0.00", "0.00",
      tf(d, "total_final"),
    ],
  },
  รายงานสรุปการจัดเก็บเงิน: {
    span: 15,
    cols: [
      ["ปี", (i) => i.year || "-"],
      ["เดือน", (i) => i.month || "-"],
      ["รายการจัดเก็บ", (i) => i.count_list || "0"],
      ["ค่าประปา", (i) => m(i.water_bill)],
      ["ค่าบริการ", (i) => m(i.equipment_maintenance_fee)],
      C.common, C.waste, C.cable,
      ["ค่าเบี้ยประกัน", C.fire[1]],
      ["รายรับอื่นๆ", (i) => m(i.other_income)],
      [
        "เพิ่ม/ลดหนี้",
        (i) => (i.add_debt || i.reduce_debt ? toFixed(i.add_debt - i.reduce_debt) : "0.00"),
      ],
      ["ปรับล่าช้า", (i) => m(i.late_payment)],
      ["ค่าต่อมิเตอร์", (i) => m(i.meter_connect_cost)],
      ["ค่าอื่นๆ", (i) => m(i.other_income2)],
      ["ปรับเศษ", (i) => m(i.change_satang)],
      [
        "รวมเป็นเงิน",
        (i) =>
          i.total_amount
            ? toFixed(
                i.water_bill + i.equipment_maintenance_fee + i.common_expenses + i.waste_cost +
                  i.other_income + i.other_income2 + i.fire_cost + i.cable_cost + i.add_debt -
                  i.reduce_debt + i.meter_connect_cost + i.late_payment + i.change_satang,
              )
            : "0.00",
      ],
    ],
  },
  รายงานสรุปรวมยกเลิกใบเสร็จ: {
    span: 15,
    cols: [
      ["ปี", (i) => i.year || "-"],
      ["เดือน", (i) => i.month || "-"],
      ["ยกเลิก (ฉบับ)", (i) => i.cancel_bill || "0"],
      ["ค่าประปา", (i) => m(i.water_bill)],
      ["ค่าบริการ", (i) => m(i.equipment_maintenance_fee)],
      C.common, C.waste, C.cable,
      ["รายรับอื่นๆ", (i) => m(i.other_income)],
      ["ค่าเบี้ยประกัน", C.fire[1]],
      [
        "เพิ่ม/ลดหนี้",
        (i) => (i.add_debt || i.reduce_debt ? toFixed(i.add_debt - i.reduce_debt) : "0.00"),
      ],
      ["ปรับล่าช้า", (i) => m(i.late_payment)],
      ["ค่าต่อมิเตอร์", (i) => m(i.meter_connect_cost)],
      ["ปรับเศษ", (i) => m(i.change_satang)],
      [
        "รวมเป็นเงิน",
        (i) => (i.total_amount || i.change_satang ? toFixed(i.total_amount + i.change_satang) : "0.00"),
      ],
    ],
  },
  รายงานแสดงรายละเอียดยกเลิกใบเสร็จ: {
    span: 22,
    cols: [
      ["เลขที่รับใบเสร็จ", (i) => i.iw_number || "-"],
      ["วันที่ใบเสร็จรับเงิน", (i) => (i.record_date ? format_date(i.record_date) + " " + i.record_time : "-")],
      C.addrSp, C.name,
      ["วันที่จดมาตร", (i) => (i.record_date ? format_date(i.record_date) + " " + i.record_time : "-")],
      ["เลขมาตรจดครั้งนี้", (i) => (i.record_water_list[0] ? i.record_water_list[0].current_gauge_number : "0")],
      ["เลขมาตรจดครั้งก่อน", (i) => (i.record_water_list[0] ? i.record_water_list[0].last_gauge_number : "0")],
      ["จำนวนหน่วยใช้", (i) => i.sum_unit_used || "0"],
      C.water, C.equip, C.common, C.waste, C.cable,
      ["ค่าใช้จ่ายอื่นๆ", (i) => m(i.other_income)],
      C.fire, C.addReduce,
      ["ค่าต่อกลับมิเตอร์", (i) => m(i.meter_connect_cost)],
      ["ค่าปรับชำระล่าช้า", (i) => m(i.late_payment)],
      ["ปรับปรุงยอดเงิน", (i) => m(i.change_satang)],
      ["รวม (บาท)", (i) => m(i.total_final)],
      ["หมายเหตุ", (i) => i.delete_remark || ""],
    ],
    foot: (d) => [
      "รวมทั้งสิ้น", "", "", "", "", "", "", "",
      tf(d, "water_bill"), tf(d, "equipment_maintenance_fee"), tf(d, "common_expenses"),
      tf(d, "waste_cost"), tf(d, "cable_cost"), tf(d, "other_income"), tf(d, "fire_cost"),
      toFixed(total(d, "add_debt") - total(d, "reduce_debt")),
      tf(d, "meter_connect_cost"), tf(d, "late_payment"), tf(d, "change_satang"),
      tf(d, "total_final"), "",
    ],
  },
  รายงานการตัดหนี้: {
    span: 17,
    cols: [
      ["ลำดับ", (i, index) => index + 1],
      C.addrSp, C.name,
      ["ประจำเดือน", C.recordDate[1]],
      ["จำนวนหน่วยใช้", (i) => i.sum_unit_used || "0"],
      ["ค่าน้ำประปา", (i) => (!i.s_water_bill && i.water_bill ? toFixed(i.water_bill) : "0.00")],
      ["ค่าส่วนกลาง", (i) => (!i.s_common_expenses && i.common_expenses ? toFixed(i.common_expenses) : "0.00")],
      ["ค่าบริการ", (i) => (!i.s_equipment_maintenance_fee && i.equipment_maintenance_fee ? toFixed(i.equipment_maintenance_fee) : "0.00")],
      ["ค่าขยะ", (i) => (!i.s_waste_cost && i.waste_cost ? toFixed(i.waste_cost) : "0.00")],
      ["ค่าเคเบิล", (i) => (!i.s_cable_cost && i.cable_cost ? toFixed(i.cable_cost) : "0.00")],
      [
        "เงินทุน/อื่นๆ",
        (i) =>
          (!i.s_other_income && i.record_other_list[0]
            ? i.record_other_list[0].cost
            : !i.s_other_income && i.other_income
            ? i.other_income
            : 0) +
          (i.s_add_debt ? i.add_debt : 0) -
          (i.s_reduce_debt ? i.reduce_debt : 0),
      ],
      ["ค่าเบี้ยประกัน", (i) => (!i.s_fire_cost && i.fire_cost ? toFixed(i.fire_cost) : "0.00")],
      ["ค่าปรับล่าช้า", (i) => m(i.late_payment)],
      ["รวมเป็นเงิน (บาท)", (i) => (i.total_final ? toFixed(i.total_final + i.late_payment) : "0.00")],
      [
        "รายละเอียด",
        (i) => i._rw?.cutOffDebt?.ref_remark || "-",
      ],
      [
        "สาเหตุ",
        (i) => (i._rw?.cutOffDebt?.ref_cause ? refCauseText(i._rw.cutOffDebt.ref_cause) : "-"),
      ],
      [
        "วันที่ตัดหนี้",
        (i) =>
          i._rw?.cutOffDebt?.ref_record_date
            ? format_date(i._rw.cutOffDebt.ref_record_date, "dd/mm/yyyy")
            : "-",
      ],
    ],
    foot: (d, totals) => [
      "รวมทั้งสิ้น", "", "", "", "",
      toFixed(total(d.filter((e) => !e.s_water_bill), "water_bill")),
      toFixed(total(d.filter((e) => !e.s_common_expenses), "common_expenses")),
      toFixed(total(d.filter((e) => !e.s_equipment_maintenance_fee), "equipment_maintenance_fee")),
      toFixed(total(d.filter((e) => !e.s_waste_cost), "waste_cost")),
      toFixed(total(d.filter((e) => !e.s_cable_cost), "cable_cost")),
      toFixed(totals),
      toFixed(total(d.filter((e) => !e.s_fire_cost), "fire_cost")),
      tf(d, "late_payment"),
      toFixed(total(d, "total_final") + total(d, "late_payment")),
      "", "", "",
    ],
  },
  "รายงานลูกหนี้-โครงการ": {
    span: 17,
    cols: [
      ["ลำดับ", (i, index) => index + 1],
      C.addrSp, C.name,
      ["ประจำเดือน", C.recordDate[1]],
      ["จำนวนหน่วยใช้", (i) => i.sum_unit_used || "0"],
      C.water, C.common,
      ["ค่าบริการ", C.equip[1]],
      C.waste, C.cable,
      [
        "เงินทุน/อื่นๆ",
        (i) =>
          Number(
            toFixed(
              (i.record_other_list[0]
                ? i.record_other_list[0].cost
                : i.other_income
                ? i.other_income
                : 0) +
                i.add_debt -
                i.reduce_debt +
                i.meter_connect_cost,
            ).replaceAll(",", ""),
          ),
      ],
      ["ค่าเบี้ยประกัน", C.fire[1]],
      ["ค่าปรับล่าช้า", (i) => m(i.late_payment)],
      ["รวมเป็นเงิน (บาท)", (i) => m(i.total_amount)],
      [
        "รายละเอียด",
        (i) => i._rw?.cutOffDebt?.ref_remark || "-",
      ],
      [
        "สาเหตุ",
        (i) => (i._rw?.cutOffDebt?.ref_cause ? refCauseText(i._rw.cutOffDebt.ref_cause) : "-"),
      ],
    ],
    foot: (d) => [
      "รวมทั้งสิ้น", "", "", "",
      total(d, "sum_unit_used"),
      tf(d, "water_bill"), tf(d, "common_expenses"), tf(d, "equipment_maintenance_fee"),
      tf(d, "waste_cost"), tf(d, "cable_cost"),
      toFixed(
        total(
          d.map((item) => ({
            cost:
              (item.record_other_list[0]
                ? item.record_other_list[0].cost
                : item.other_income
                ? item.other_income
                : 0) +
              item.add_debt -
              item.reduce_debt +
              item.meter_connect_cost,
          })),
          "cost",
        ),
      ),
      tf(d, "fire_cost"), tf(d, "late_payment"), tf(d, "total_amount"),
      "", "",
    ],
  },
  "รายงานลูกหนี้คงค้าง ณ เดือน": {
    span: 14,
    cols: [C.addrZW, C.name, C.recordDate, C.unit, C.water, C.equip, C.common, C.waste, C.cable, C.otherMeter, C.fire, C.addReduce, C.late, C.sum],
    foot: (d) => [
      "รวมทั้งสิ้น", `${d.length} งวด`, "", "",
      tf(d, "water_bill"), tf(d, "equipment_maintenance_fee"), tf(d, "common_expenses"),
      tf(d, "waste_cost"), tf(d, "cable_cost"),
      toFixed(total(d, "other_income") + total(d, "meter_connect_cost")),
      tf(d, "fire_cost"),
      toFixed(total(d, "add_debt") - total(d, "reduce_debt")),
      tf(d, "late_payment"), tf(d, "total_amount"),
    ],
  },
};

/* หาบิลซ้ำ (อาคาร + บ้านเลขที่ + วันที่จดมาตร เหมือนกัน — ไฮไลต์เฉพาะแถวที่ 2 เป็นต้นไป) — ใช้แสดงผลเท่านั้น ไม่กระทบยอด/ลำดับ */
const dupKeyOf = (i) =>
  [i.building || "", i.address_number || "", i.datetime_create ? format_date(i.datetime_create, "dd/mm/yyyy") : ""].join("|");
function findDupIndexes(data) {
  // คืน index ของแถวที่ "เคยเจอคีย์นี้มาแล้วก่อนหน้า" (แถวแรกของกลุ่มไม่นับ)
  const seen = new Set();
  const dup = new Set();
  (data || []).forEach((i, idx) => {
    if (!i.address_number || !i.datetime_create) return;
    const k = dupKeyOf(i);
    if (seen.has(k)) dup.add(idx);
    else seen.add(k);
  });
  return dup;
}
const DUP_BG = "#fdecec";
const DUP_TITLE = "บิลซ้ำ: ห้องนี้มีบิลงวดนี้ซ้ำเกินมา (ซ้ำกับแถวก่อนหน้า)";

/* ----------------------------------------------------------------------------
 * ตารางรายงานแบบ data-driven
 * --------------------------------------------------------------------------*/
function ReportTable({ cfg, data, totals }) {
  if (!cfg || !data) return null;
  const dupSet = cfg.markDup ? findDupIndexes(data) : null;
  const span = cfg.span || cfg.cols.length;
  return (
    <>
      <thead className={cfg.sticky ? "position-sticky" : undefined}>
        <tr className="bg-dark-blue text-white">
          {cfg.cols.map((c, i) => (
            <th key={i} className="text-nowrap text-left">
              {c[0]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.length === 0 && (
          <tr>
            <td colSpan={span} className="text-center">
              ไม่มีข้อมูล
            </td>
          </tr>
        )}
        {data.map((item, index) => {
          const isDup = !!dupSet && dupSet.has(index);
          const rowStyle = isDup ? { backgroundColor: DUP_BG, boxShadow: "none" } : undefined;
          return (
            <tr
              key={"key_" + index}
              style={rowStyle}
              title={isDup ? DUP_TITLE : undefined}
              data-dup={isDup ? "1" : undefined}
            >
              {cfg.cols.map((c, ci) => (
                <td key={ci} className={c[2] ? "text-nowrap" : undefined} style={rowStyle}>
                  {c[1](item, index, data)}
                </td>
              ))}
            </tr>
          );
        })}
        {cfg.foot && (
          <tr>
            {cfg.foot(data, totals).map((v, i) => (
              <td key={i}>{v}</td>
            ))}
          </tr>
        )}
      </tbody>
    </>
  );
}

export default class Report extends Component {
  constructor(props) {
    super(props);

    this.state = {
      page: 1,
      search: "",
      loading_p2: false,

      data: null,
      record_water_list: null,
      data_customer: null,
      totals: null,

      report_type: "",
      print_type: "",
      address_number: "",
      full_name: "",
      start_date: "",
      end_date: "",
      cut_off_date: "",
      title_report: { value: "0", label: "ทั้งหมด" },
      select_month: "",
      ref_cause: "0",
      ref_cause_text: "0",


      data_payment: [],
      payment_type: null,
      status_transfer: { value: "0", label: "ทั้งหมด" },
      month_count: "",

      percent: 0,
    };
    this.tableRef = React.createRef();
  }
  async componentDidMount() {
    loading(false);
    await this.GetCustomerAll();
    await this.GetPaymentAll();
  }

  GetCustomerAll = async () => {
    let data = { data_search: "", transfer_date: "", status_transfer: "" };
    let result = await GET(
      tokens,
      "v1/TH/customer-manage/filter-search/page/0/" + getStorage("project_id"),
      data,
    );
    if (result && result.status) {
      this.setState({
        data_customer: result.data?.data?.map((item) => ({
          value: item.address_number,
          label: "อาคาร " + item.building + " ห้อง " + item.address_number,
          ref_customer_id: item.ref_customer_id,
          full_name: item.full_name,
          status_transfer: item.status_transfer,
        })),
      });
    }
  };
  GetPaymentAll = async () => {
    let data = { data_search: "" };
    let result = await GET(
      tokens,
      "v1/TH/document-manage/pay-list/filter-search/page/0",
      data,
    );
    if (result && result.status) {
      this.setState({
        data_payment: result.data.data.map((item) => ({
          label: item.title,
          value: item.code,
        })),
      });
    }
  };
  // ดึงพารามิเตอร์ filter พื้นฐาน (address_number / start_date / end_date [/ status_transfer])
  baseParams = (withStatus) => {
    const p = {
      address_number: this.state.address_number ? this.state.address_number.value : "",
      start_date: this.state.start_date ? this.state.start_date : "",
      end_date: this.state.end_date ? this.state.end_date : "",
    };
    if (withStatus) {
      p.status_transfer = this.state.status_transfer ? this.state.status_transfer.value : "";
    }
    return p;
  };
  // map report_type -> getReport
  reportRunners = {
    รายงานหนี้ค้างชำระ: () => this.getReport1(),
    รายงานสถานะห้องชุด: () => this.getReport2(),
    รายงานแจ้งหนี้ประจำเดือน: () => this.getReport3(),
    รายงานแจ้งหนี้คงค้างย้อนหลัง: () => this.getReport4(),
    รายงานใบเสร็จรับเงิน: () => this.getReport5(),
    รายงานลูกหนี้: () => this.getReport6(),
    "รายงานอื่น ๆ ประจำเดือน": () => this.getReport7(),
    รายงานสรุปการจัดเก็บเงิน: () => this.getReport8(),
    รายงานสรุปรวมยกเลิกใบเสร็จ: () => this.getReport9(),
    รายงานแสดงรายละเอียดยกเลิกใบเสร็จ: () => this.getReport11(),
    รายงานการตัดหนี้: () => this.getReport10(),
    "รายงานลูกหนี้-โครงการ": () => this.getReport12(),
    "รายงานลูกหนี้คงค้าง ณ เดือน": () => this.getReport13(),
  };
  runReport = (type) => {
    const fn = this.reportRunners[type];
    if (fn) fn();
  };
  Print = async () => {
    switch (this.state.print_type) {
      case "พิมพ์รายงานหนี้ค้างชำระ":
        this.printReport1();
        break;
      case "พิมพ์สถานะห้องชุด":
        this.printReport2();
        break;
      case "รายงานแจ้งหนี้ประจำเดือน":
        this.printReport3();
        break;
      case "พิมพ์รายงานแจ้งหนี้คงค้างย้อนหลัง":
        this.printReport4();
        break;
      case "พิมพ์ใบเสร็จรับเงินประจำเดือน":
        this.printReport5();
        break;
      case "พิมพ์รายงานลูกหนี้":
        this.printReport6();
        break;
      case "พิมพ์รายงานอื่น ๆ ประจำเดือน":
        this.printReport7();
        break;
      case "พิมพ์รายงานสรุปการจัดเก็บเงิน":
        this.printReport8();
        break;
      case "พิมพ์รายงานสรุปรวมยกเลิกใบเสร็จ":
        this.printReport9();
        break;
      case "พิมพ์รายงานแสดงรายละเอียดยกเลิกใบเสร็จ":
        this.printReport11();
        break;
      case "พิมพ์รายงานรายงานตัดลูกหนี้":
        this.printReport10();
        break;
      case "แสดงสรุป":
        this.printReport10();
        break;
      case "แสดงสรุป12":
        this.printReport12();
        break;
      case "พิมพ์รายงานลูกหนี้คงค้าง ณ เดือน":
        this.printReport13();
        break;
      case "แสดงรายละเอียด":
        this.printReport10();
        break;
      default:
        break;
    }
  };
  // พิมพ์รายงานหนี้ค้างชำระ
  getReport1 = async () => {
    loading(true);
    if (this.state.start_date && !this.state.end_date) {
      this.setState({ end_date: format_date(new Date(), "yyyy-mm-dd", "en") });
    }
    setTimeout(async () => {
      let result = await GET(
        tokens,
        "v1/TH/report/overdue/filter-search/" + getStorage("project_id"),
        this.baseParams(),
      );
      if (result && result.status) {
        let array = result.data.data_water;
        for (let item of array) settleFlags(item);
        const data_water = Object.values(
          array.reduce((p, v) => {
            const old = p[v.ref_customer_id];
            if (!old) {
              let total = 0;
              total = total + v.water_bill;
              total = total + v.waste_cost;
              total = total - v.reduce_debt;
              total = total + v.other_income;
              total = total + v.fire_cost;
              total = total + v.meter_connect_cost;
              total = total + v.late_payment;
              total = total + v.equipment_maintenance_fee;
              total = total + v.common_expenses;
              total = total + v.cable_cost;
              total = total + v.add_debt;
              p[v.ref_customer_id] = {
                ...v,
                count_installment: 1,
                total_amount: total,
                installment: 1,
                unit_used: v.unit_used,
                water_bill: v.water_bill,
                waste_cost: v.waste_cost,
                reduce_debt: v.reduce_debt,
                other_income: v.other_income,
                fire_cost: v.fire_cost,
                meter_connect_cost: v.meter_connect_cost,
                late_payment: v.late_payment,
                equipment_maintenance_fee: v.equipment_maintenance_fee,
                common_expenses: v.common_expenses,
                cable_cost: v.cable_cost,
                add_debt: v.add_debt,
                status_transfer: this.state.data_customer.filter(
                  (e) => e.value === v.address_number,
                )[0].status_transfer,
              };
            } else {
              let total = 0;
              !v.s_water_bill && (total = total + v.water_bill);
              !v.s_waste_cost && (total = total + v.waste_cost);
              !v.s_reduce_debt && (total = total - v.reduce_debt);
              !v.s_other_income && (total = total + v.other_income);
              !v.s_fire_cost && (total = total + v.fire_cost);
              !v.s_meter_connect_cost && (total = total + v.meter_connect_cost);
              !v.s_late_payment && (total = total + v.late_payment);
              !v.s_equipment_maintenance_fee &&
                (total = total + v.equipment_maintenance_fee);
              !v.s_common_expenses && (total = total + v.common_expenses);
              !v.s_cable_cost && (total = total + v.cable_cost);
              !v.s_add_debt && (total = total + v.add_debt);

              p[v.ref_customer_id] = {
                ...old,
                total_amount: old.total_amount + total,
                count_installment: old.count_installment + 1,
                overdue_amount: old.overdue_amount + total,
                unit_used: v.unit_used + old.unit_used,
                water_bill: !v.s_water_bill ? old.water_bill + v.water_bill : old.water_bill,
                waste_cost: !v.s_waste_cost ? old.waste_cost + v.waste_cost : old.waste_cost,
                reduce_debt: !v.s_reduce_debt ? old.reduce_debt + v.reduce_debt : old.reduce_debt,
                other_income: !v.s_other_income ? old.other_income + v.other_income : old.other_income,
                fire_cost: !v.s_fire_cost ? old.fire_cost + v.fire_cost : old.fire_cost,
                meter_connect_cost: !v.s_meter_connect_cost
                  ? old.meter_connect_cost + v.meter_connect_cost
                  : old.meter_connect_cost,
                late_payment: !v.s_late_payment ? old.late_payment + v.late_payment : old.late_payment,
                equipment_maintenance_fee: !v.s_equipment_maintenance_fee
                  ? old.equipment_maintenance_fee + v.equipment_maintenance_fee
                  : old.equipment_maintenance_fee,
                common_expenses: !v.s_common_expenses ? old.common_expenses + v.common_expenses : old.common_expenses,
                cable_cost: !v.s_cable_cost ? old.cable_cost + v.cable_cost : old.cable_cost,
                add_debt: !v.s_add_debt ? old.add_debt + v.add_debt : old.add_debt,
                installment: old.installment + 1,
                status_transfer: this.state.data_customer.filter(
                  (e) => e.value === v.address_number,
                )[0].status_transfer,
              };
            }
            return p;
          }, {}),
        );
        let array_list = data_water.filter(
          (e) =>
            !this.state.status_transfer ||
            e.status_transfer.toString() === this.state.status_transfer.value ||
            this.state.status_transfer.value === "0",
        );
        array_list.sort((a, b) =>
          a.address_number.localeCompare(b.address_number, undefined, {
            numeric: true,
            sensitivity: "base",
          }),
        );
        this.setState({ data: array_list });
      }
      loading(false);
    }, 10);
  };
  printReport1 = () => report1(this.state.data || [], this.state.end_date);
  // พิมพ์สถานะห้องชุด V2
  getReport2 = async () => {
    loading(true);
    let result = await GET(
      tokens,
      "v1/TH/report/overdue/filter-search/" + getStorage("project_id"),
      this.baseParams(),
    );
    if (result && result.status) {
      let obj = result.data.data_water;
      for (let item of obj) {
        const idx = this.state.data_customer.findIndex(
          (e) => e.ref_customer_id === item.ref_customer_id,
        );
        if (idx !== -1) {
          item.full_name = this.state.data_customer[idx].full_name;
          item.status_transfer = this.state.data_customer[idx].status_transfer;
        }
        settleFlags(item);
      }
      obj = obj.filter(
        (item) =>
          this.state.data_customer.findIndex(
            (e) => e.ref_customer_id === item.ref_customer_id,
          ) !== -1,
      );
      if (this.state.status_transfer.value === "0") {
        this.setState({ data: obj });
      } else {
        this.setState({
          data: obj.filter(
            (item) => item.status_transfer === Number(this.state.status_transfer.value),
          ),
        });
      }
    }
    loading(false);
  };
  printReport2 = () =>
    report2(this.state.data || [], this.state.start_date, this.state.status_transfer);
  // รายงานแจ้งหนี้ประจำเดือน
  getReport3 = async () => {
    loading(true);
    let result = await GET(
      tokens,
      "v1/TH/report/overdue-month/filter-search/" + getStorage("project_id"),
      this.baseParams(true),
    );
    if (result && result.status) {
      let obj = result.data.data_water;
      // NOTE 4 พ.ย. 2569 (แทนมติ 3 พ.ย.): แสดงยอดเต็มของทุกบิลตามที่ backend ส่ง (ชำระแล้วก็เติมค่าจากประวัติตอนชำระมาให้แล้ว
      // snapshot_filled) จึงไม่เรียก settleFlags · เพิ่ม/ลดหนี้ = ของบิล + ที่ทำตอนออกใบเสร็จ (add_on_*)
      // total_amount ของ backend ไม่รวม add_on_* จึงคำนวณใหม่ที่นี่ · report 1,2,4,6 ยังใช้ settleFlags ตามเดิม
      for (let item of obj) {
        item.add_debt = Number(item.add_debt || 0) + Number(item.add_on_add_debt || 0);
        item.reduce_debt = Number(item.reduce_debt || 0) + Number(item.add_on_reduce_debt || 0);
        for (const { k } of BILL_FIELDS) item[k] = norm(item[k]);
        item.total_amount = norm(sumBillFields(item));
        // backend รุ่นก่อนไม่มี pay_status → เดาจาก status_pay (2 ชำระแล้ว / 3 ตัดหนี้ / อื่นๆ ค้างชำระ) ให้สรุปท้าย PDF ลงตัว
        if (!item.pay_status && item.status_pay !== undefined && item.status_pay !== null) {
          const sp = Number(item.status_pay);
          item.pay_status = sp === 2 ? "paid" : sp === 3 ? "written_off" : "unpaid";
          item.pay_status_text = sp === 2 ? "ชำระแล้ว" : sp === 3 ? "ตัดหนี้" : "ค้างชำระ";
        }
      }
      this.setState({ data: obj });
    }
    loading(false);
  };
  printReport3 = () => report3(this.state.data || [], this.state.start_date);
  // พิมพ์รายงานแจ้งหนี้คงค้างย้อนหลัง
  getReport4 = async () => {
    loading(true);
    let result = await GET(
      tokens,
      "v1/TH/report/overdue/filter-search/" + getStorage("project_id"),
      this.baseParams(true),
    );
    if (result && result.status) {
      let obj = result.data.data_water;
      for (let item of obj) settleFlags(item, ["meter_connect_cost"]);
      this.setState({ data: obj });
    }
    loading(false);
  };
  printReport4 = () => report4(this.state.data || [], this.state.start_date);
  // พิมพ์ใบเสร็จรับเงิน
  getReport5 = async () => {
    loading(true);
    let result = await GET(
      tokens,
      "v1/TH/report/receipt/filter-search/" + getStorage("project_id"),
      this.baseParams(),
    );
    if (result && result.status) {
      let array = result.data.data
        .filter((e) => e.status_bill === 1 && e.rw_number)
        .sort(fieldSorter(["iw_number", "-datetime_create"]));
      for (let item of array) {
        processPayments(item, true);
      }
      this.setState({ data: array, record_water_list: array });
    }
    loading(false);
  };
  printReport5 = () =>
    report5(
      this.state.record_water_list
        ? this.state.record_water_list.sort(fieldSorter(["iw_number", "-datetime_create"]))
        : [],
    );
  // พิมพ์รายงานลูกหนี้
  getReport6 = async () => {
    loading(true);
    let result = await GET(
      tokens,
      "v1/TH/report/overdue/filter-search/" + getStorage("project_id"),
      this.baseParams(),
    );
    if (result && result.status) {
      let array = result.data;
      for (let item of array.data_water) settleFlags(item);
      let data_water = Object.values(
        array.data_water.reduce((p, v) => {
          v.late_payment = norm(v.late_payment);
          v.total_amount = norm(v.total_amount);

          const old = p[v.ref_customer_id];
          if (!old) {
            let total = 0;
            total = total + v.water_bill;
            total = total + v.waste_cost;
            total = total - v.reduce_debt;
            total = total + v.other_income;
            total = total + v.fire_cost;
            total = total + v.meter_connect_cost;
            total = total + v.late_payment;
            total = total + v.equipment_maintenance_fee;
            total = total + v.common_expenses;
            total = total + v.cable_cost;
            total = total + v.add_debt;
            p[v.ref_customer_id] = {
              ...v,
              count_installment: 1,
              total_amount: total,
              other_income: v.other_income + v.meter_connect_cost,
            };
          } else {
            let total = 0;
            !v.s_water_bill && (total = total + v.water_bill);
            !v.s_waste_cost && (total = total + v.waste_cost);
            !v.s_reduce_debt && (total = total - v.reduce_debt);
            !v.s_other_income && (total = total + v.other_income);
            !v.s_fire_cost && (total = total + v.fire_cost);
            !v.s_meter_connect_cost && (total = total + v.meter_connect_cost);
            !v.s_late_payment && (total = total + v.late_payment);
            !v.s_equipment_maintenance_fee && (total = total + v.equipment_maintenance_fee);
            !v.s_common_expenses && (total = total + v.common_expenses);
            !v.s_cable_cost && (total = total + v.cable_cost);
            !v.s_add_debt && (total = total + v.add_debt);

            p[v.ref_customer_id] = {
              ...old,
              count_installment: old.count_installment + 1,
              overdue_amount: old.overdue_amount + total,
              unit_used: old.unit_used + v.unit_used,
              water_bill: old.water_bill + v.water_bill,
              waste_cost: old.waste_cost + v.waste_cost,
              reduce_debt: old.reduce_debt + v.reduce_debt,
              other_income: old.other_income + v.other_income + v.meter_connect_cost,
              fire_cost: old.fire_cost + v.fire_cost,
              meter_connect_cost: old.meter_connect_cost + v.meter_connect_cost,
              late_payment: old.late_payment + v.late_payment,
              equipment_maintenance_fee: old.equipment_maintenance_fee + v.equipment_maintenance_fee,
              common_expenses: old.common_expenses + v.common_expenses,
              cable_cost: old.cable_cost + v.cable_cost,
              add_debt: old.add_debt + v.add_debt,
            };
          }
          return p;
        }, {}),
      );
      if (this.state.month_count) {
        data_water = data_water.filter(
          (item) => this.state.month_count <= item.count_installment,
        );
      }
      if (this.state.title_report && this.state.title_report.value === "1") {
        this.setState({ data: data_water.filter((item) => item.unit_used === 0) });
      } else if (this.state.title_report && this.state.title_report.value === "2") {
        this.setState({ data: data_water.filter((item) => item.unit_used > 0) });
      } else {
        this.setState({ data: data_water });
      }
    }
    loading(false);
  };
  printReport6 = () =>
    report6(this.state.data || [], this.state.title_report ? this.state.title_report.value : "0");
  // พิมพ์รายงานอื่น ๆ ประจำเดือน
  getReport7 = async () => {
    loading(true);
    let result = await GET(
      tokens,
      "v1/TH/report/receipt/filter-search/" + getStorage("project_id"),
      this.baseParams(),
    );
    if (result && result.status) {
      let array = result.data.data
        .filter((item) => item.record_other_list.length !== 0)
        .filter((item) => item.status_bill === 1)
        .filter(
          (item) =>
            this.state.payment_type === null ||
            (this.state.payment_type &&
              this.state.payment_type.value === item.record_other_list[0].code),
        )
        .map((e) => {
          const otherCost = Number(e.record_other_list?.[0]?.cost) || 0;
          return {
            ...e,
            total_final: e.status_bill === 2 ? 0 : otherCost,
            total_amount: e.status_bill === 2 ? 0 : e.total_amount,
          };
        });
      let totals = 0;
      for (let item of array) {
        totals += item.record_other_list[0] ? item.record_other_list[0].cost : 0;
      }
      this.setState({ data: array, totals });
    }
    loading(false);
  };
  printReport7 = () => report7(this.state.data || []);
  // พิมพ์รายงานสรุปการจัดเก็บเงิน
  getReport8 = async () => {
    loading(true);
    let result = await GET(
      tokens,
      "v1/TH/report/receipt/filter-search/" + getStorage("project_id"),
      this.baseParams(),
    );
    if (result && result.status) {
      let array = result.data.data
        .filter((e) => e.status_bill === 1 && e.rw_number)
        .sort(fieldSorter(["iw_number", "-datetime_create"]));
      for (let item of array) processPayments(item, false);

      // group ตาม year/month จาก record_date
      const sumFields = [
        "water_bill", "waste_cost", "other_income", "meter_connect_cost", "late_payment",
        "fire_cost", "equipment_maintenance_fee", "common_expenses", "cable_cost", "add_debt",
        "reduce_debt", "total_amount", "total_final", "change_satang",
      ];
      let d = [];
      for (let item of array) {
        const year = format_date(item.record_date, "yyyy");
        const month = format_date(item.record_date, "m");
        let other_income2 = 0;
        for (let item2 of item.record_other_list || []) {
          other_income2 += Number(item2.cost) || 0;
        }
        let group = d.find((e) => e.year === year && e.month === month);
        if (group) {
          sumFields.forEach((f) => (group[f] += Number(item[f]) || 0));
          group.other_income2 += other_income2;
          group.count_list += 1;
        } else {
          group = { year, month, datetime_create: item.record_date, other_income2, count_list: 1 };
          sumFields.forEach((f) => (group[f] = Number(item[f]) || 0));
          d.push(group);
        }
      }
      this.setState({ data: d });
    }
    loading(false);
  };
  printReport8 = () => report8(this.state.data || []);
  // พิมพ์รายงานสรุปรวมยกเลิกใบเสร็จ
  getReport9 = async () => {
    loading(true);
    let data = {
      start_date: this.state.start_date ? this.state.start_date : "",
      end_date: this.state.end_date ? this.state.end_date : "",
    };
    let result = await GET(
      tokens,
      "v1/TH/report/receipt/filter-search/" + getStorage("project_id"),
      data,
    );
    if (result && result.status) {
      let array = result.data.data.filter((item) => item.status_bill === 2);
      for (let item of array) {
        for (const element of item.record_water_list) {
          item.late_payment = norm(item.late_payment);
          item.common_expenses = norm(item.common_expenses);
          item.cable_cost = norm(item.cable_cost);
          item.fire_cost = norm(item.fire_cost);

          element.late_payment = norm(element.late_payment);
          element.common_expenses = norm(element.common_expenses);
          element.cable_cost = norm(element.cable_cost);
          element.fire_cost = norm(element.fire_cost);

          let total_cost = 0;
          if (item.s_add_debt === false) {
            item.add_debt = 0;
            element.add_debt = 0;
          } else {
            total_cost += item.add_debt;
          }
          if (item.s_cable_cost === false) {
            item.cable_cost = 0;
            element.cable_cost = 0;
          } else {
            total_cost += item.cable_cost;
          }
          if (item.s_common_expenses === false) {
            item.common_expenses = 0;
            element.common_expenses = 0;
          } else {
            total_cost += item.common_expenses;
          }
          if (item.s_equipment_maintenance_fee === false) {
            item.equipment_maintenance_fee = 0;
            element.equipment_maintenance_fee = 0;
          } else {
            total_cost += item.equipment_maintenance_fee;
          }
          if (item.s_fire_cost === false) {
            item.fire_cost = 0;
            element.fire_cost = 0;
          } else {
            total_cost += item.fire_cost;
          }
          if (item.s_late_payment === false) {
            item.late_payment = 0;
            element.late_payment = 0;
          } else {
            total_cost += item.late_payment;
          }
          if (item.s_meter_connect_cost === false) {
            item.meter_connect_cost = 0;
            element.meter_connect_cost = 0;
          } else {
            total_cost += item.meter_connect_cost;
          }
          if (item.s_other_income === false) {
            item.other_income = 0;
            element.other_income = 0;
          } else {
            total_cost += item.other_income;
          }
          if (item.s_reduce_debt === false) {
            item.reduce_debt = 0;
            element.reduce_debt = 0;
          } else {
            total_cost -= item.reduce_debt;
          }
          if (item.s_waste_cost === false) {
            item.waste_cost = 0;
            element.waste_cost = 0;
          } else {
            total_cost += item.waste_cost;
          }
          if (item.s_water_bill === false) {
            item.water_bill = 0;
            element.water_bill = 0;
          } else {
            total_cost += item.water_bill;
          }

          item.add_debt += item.add_on_add_debt;
          item.reduce_debt += item.add_on_reduce_debt;

          total_cost += item.add_on_add_debt;
          total_cost -= item.add_on_reduce_debt;

          element.add_debt += item.add_on_add_debt;
          element.reduce_debt += item.add_on_reduce_debt;

          item.total_amount = total_cost;
          element.total_amount = total_cost;

          item.total_amount = norm(item.total_amount);
          element.total_amount = norm(element.total_amount);
        }
      }
      const data_water = Object.values(
        array.reduce((p, v) => {
          const old = p[format_date(v.datetime_create, "mm-yyyy")];
          if (!old) {
            p[format_date(v.datetime_create, "mm-yyyy")] = {
              ...v,
              cancel_bill: 1,
              year: format_date(v.datetime_create, "yyyy"),
              month: format_date(v.datetime_create, "m"),
            };
          } else {
            p[format_date(v.datetime_create, "mm-yyyy")] = {
              ...old,
              cancel_bill: old.cancel_bill + 1,
              water_bill: old.water_bill + v.water_bill,
              waste_cost: old.waste_cost + v.waste_cost,
              reduce_debt: old.reduce_debt + v.reduce_debt,
              other_income: old.other_income + v.other_income,
              fire_cost: old.fire_cost + v.fire_cost,
              meter_connect_cost: old.meter_connect_cost + v.meter_connect_cost,
              late_payment: old.late_payment + v.late_payment,
              equipment_maintenance_fee: old.equipment_maintenance_fee + v.equipment_maintenance_fee,
              common_expenses: old.common_expenses + v.common_expenses,
              cable_cost: old.cable_cost + v.cable_cost,
              add_debt: old.add_debt + v.add_debt,
              total_amount: old.total_amount + v.total_amount,
              total_final: old.total_final + v.total_final,
              change_satang: old.change_satang + v.change_satang,
            };
          }
          return p;
        }, {}),
      );
      this.setState({ data: data_water });
    }
    loading(false);
  };
  printReport9 = () => report9(this.state.data || []);
  // รายงานแสดงรายละเอียดยกเลิกใบเสร็จ
  getReport11 = async () => {
    loading(true);
    let result = await GET(
      tokens,
      "v1/TH/report/receipt/filter-search/" + getStorage("project_id"),
      this.baseParams(),
    );
    if (result && result.status) {
      let array = result.data.data.filter((item) => item.status_bill === 2);
      for (let item of array) {
        item.late_payment = norm(item.late_payment);
        item.common_expenses = norm(item.common_expenses);
        item.cable_cost = norm(item.cable_cost);
        item.fire_cost = norm(item.fire_cost);
        item.add_debt = 0;
        item.cable_cost = 0;
        item.common_expenses = 0;
        item.equipment_maintenance_fee = 0;
        item.fire_cost = 0;
        item.late_payment = 0;
        item.meter_connect_cost = 0;
        item.other_income = 0;
        item.reduce_debt = 0;
        item.waste_cost = 0;
        item.water_bill = 0;
        item.total_final = 0;

        for (const element of item.record_water_list) {
          element.late_payment = norm(element.late_payment);
          element.common_expenses = norm(element.common_expenses);
          element.cable_cost = norm(element.cable_cost);
          element.fire_cost = norm(element.fire_cost);
          element.total_amount = norm(element.total_amount);

          item.add_debt += element.add_debt;
          item.cable_cost += element.cable_cost;
          item.common_expenses += element.common_expenses;
          item.equipment_maintenance_fee += element.equipment_maintenance_fee;
          item.fire_cost += element.fire_cost;
          item.late_payment += element.late_payment;
          item.meter_connect_cost += element.meter_connect_cost;
          item.other_income += element.cost || element.other_income;
          item.reduce_debt += element.reduce_debt;
          item.waste_cost += element.waste_cost;
          item.water_bill += element.water_bill;
        }

        let _total =
          item.water_bill + item.equipment_maintenance_fee + item.common_expenses + item.waste_cost +
          item.cable_cost + item.other_income + item.fire_cost + item.add_debt + item.meter_connect_cost +
          item.late_payment + item.change_satang - item.reduce_debt;

        item.total_amount += norm(_total);
        item.total_final += norm(_total);
      }

      let totals = 0;
      for (let item of array) {
        totals += item.record_other_list[0]
          ? item.record_other_list[0].cost
          : item.total_final
          ? item.total_final
          : 0;
      }
      this.setState({ data: array, totals });
    }
    loading(false);
  };
  printReport11 = () => report11(this.state.data || []);
  // พิมพ์รายงานรายงานตัดลูกหนี้
  getReport10 = async () => {
    loading(true);
    let result = await GET(
      tokens,
      "v1/TH/report/clear-bill/filter-search/" + getStorage("project_id"),
      this.baseParams(true),
    );
    if (result && result.status) {
      let array = result.data.data
        .map((item) => ({ ...item, _rw: normalizeRwList(item.record_water_list) }))
        .filter(
          (item) =>
            item.record_other_list.length === 0 &&
            item.record_water_list !== 0 &&
            item.record_water_list &&
            item._rw.statusPay === 3,
        );
      let totals = 0;
      if (this.state.cut_off_date) {
        array = array.filter(
          (item) =>
            item._rw.cutOffDebt &&
            format_date(item._rw.cutOffDebt.ref_record_date, "yyyy-mm-dd") ===
              format_date(this.state.cut_off_date, "yyyy-mm-dd"),
        );
      }
      for (let item of array) {
        item.late_payment = norm(item.late_payment);
        item.common_expenses = norm(item.common_expenses);
        item.cable_cost = norm(item.cable_cost);
        item.fire_cost = norm(item.fire_cost);
        totals +=
          !item.s_other_income && item.record_other_list[0]
            ? item.record_other_list[0].cost
            : !item.s_other_income && item.other_income
            ? item.other_income
            : 0;
        item.total_final =
          (!item.s_water_bill ? item.water_bill || 0 : 0) +
          (!item.s_equipment_maintenance_fee ? item.equipment_maintenance_fee || 0 : 0) +
          (!item.s_common_expenses ? item.common_expenses || 0 : 0) +
          (!item.s_waste_cost ? item.waste_cost || 0 : 0) +
          (!item.s_other_income && item.record_other_list[0]
            ? item.record_other_list[0].cost
            : !item.s_other_income && item.other_income
            ? item.other_income
            : 0) +
          (!item.s_fire_cost ? item.fire_cost || 0 : 0) +
          (item.s_add_debt ? item.add_debt || 0 : 0) -
          (item.s_reduce_debt ? item.reduce_debt || 0 : 0);
        item.total_final = norm(item.total_final);
      }
      this.setState({ data: array, ref_cause_text: this.state.ref_cause_text, totals });
    }
    loading(false);
  };
  printReport10 = () => report10(this.state.data || [], "ลูกค้าชำระแล้ว");
  // พิมพ์รายงานรายงานลูกหนี้-โครงการ
  getReport12 = async () => {
    loading(true);
    let result = await GET(
      tokens,
      "v1/TH/report/clear-bill/filter-search/" + getStorage("project_id"),
      this.baseParams(true),
    );
    if (result && result.status) {
      let array = result.data.data
        .map((item) => ({ ...item, _rw: normalizeRwList(item.record_water_list) }))
        .filter(
          (item) =>
            item.record_other_list.length === 0 &&
            item.record_water_list !== 0 &&
            item.record_water_list &&
            item._rw.cutOffDebt?.ref_cause === 2,
        );
      for (let item of array) {
        item.late_payment = norm(item.late_payment);
        item.common_expenses = norm(item.common_expenses);
        item.cable_cost = norm(item.cable_cost);
        item.fire_cost = norm(item.fire_cost);
        item.total_amount =
          item.add_debt + item.cable_cost + item.common_expenses + item.equipment_maintenance_fee +
          item.fire_cost + item.late_payment + item.meter_connect_cost + item.other_income +
          item.waste_cost + item.water_bill - item.reduce_debt;
      }
      this.setState({ data: array, ref_cause_text: this.state.ref_cause_text });
    }
    loading(false);
  };
  printReport12 = () => report12(this.state.data || [], "ลูกค้าเปลี่ยนสัญญา (บรรจุใหม่) ห้อง กคช.");
  // พิมพ์รายงานลูกหนี้คงค้าง ณ เดือน
  getReport13 = async () => {
    loading(true);
    // NOTE เดิม setState แล้วรอ setTimeout 10ms ให้ state อัปเดตก่อนอ่านค่า
    //      แต่ setState ไม่รับประกันว่าจะเสร็จภายใน 10ms — บางครั้งยิง API ด้วย end_date ที่ยังว่าง
    //      ตอนนี้คำนวณค่าไว้ในตัวแปรก่อน แล้วใช้ค่านั้นทั้งยิง API และ setState ไม่ต้องรอเวลา
    const end_date = this.state.end_date || format_date(new Date(), "yyyy-mm-dd", "en");
    if (!this.state.end_date) this.setState({ end_date });

    {
      let data = {
        address_number: this.state.address_number ? this.state.address_number.value : "",
        end_date,
      };
      let result = await GET(
        tokens,
        "v1/TH/report/overdue/raw-data/" + getStorage("project_id"),
        data,
      );
      if (result && result.status) {
        let water = result.data.data_water;
        // NOTE รายงานนี้เดิมหักยอดจาก data_receipt อย่างเดียว ซึ่งพลาด 2 จุด
        //   1. ธง s_* ของใบเสร็จเป็นสถานะระดับใบ ใบเดียวผูกได้หลายงวด จึงไป zero ช่องนั้นของทุกงวดที่ผูกไว้
        //      (งวดที่มี transaction_payments แล้ว ให้ยึดลิสต์ระดับงวดแทน แม่นกว่า และไม่นับใบที่ถูกยกเลิก)
        //   2. ไม่ได้หักรายการตัดหนี้ (writeoff_list) เลย ยอดที่ตัดหนี้ไปแล้วจึงยังโผล่เป็นหนี้ค้าง
        // งวดเก่าที่ยังไม่มี transaction_payments (ข้อมูลก่อนมีฟีเจอร์นี้) ยังใช้วิธีเดิมตามใบเสร็จ
        const cutoff = String(end_date).slice(0, 10);
        const untilCutoff = (list) =>
          (list || []).filter((e) => !e.datetime_create || String(e.datetime_create).slice(0, 10) <= cutoff);
        for (let item of water) {
          item.transaction_payments = untilCutoff(item.transaction_payments);
          item.writeoff_list = untilCutoff(item.writeoff_list);
        }
        // ใบเสร็จที่เคยสร้าง transaction_payments ไว้ = รู้อยู่แล้วว่าเงินไปตัดงวดไหนช่องไหน
        // ห้ามเอาธงระดับใบมา zero งวดอื่นในใบเดียวกันซ้ำ (ใบเดียวผูกหลายงวดแต่จ่ายจริงงวดเดียวก็มี)
        const iwWithTnx = new Set();
        for (let item of water) {
          for (let p of item.transaction_payments || []) {
            if (p.ref_iw_number) iwWithTnx.add(p.ref_iw_number);
          }
        }
        for (let item of result.data.data_receipt) {
          if (iwWithTnx.has(item.iw_number)) continue;
          for (let item_water of item.record_water_list) {
            let index = water.findIndex((e) => e.record_water_id === item_water);
            if (index === -1 || water[index].transaction_payments?.length) continue;
            if (item.s_add_debt) water[index].add_debt = 0;
            if (item.s_cable_cost) water[index].cable_cost = 0;
            if (item.s_common_expenses) water[index].common_expenses = 0;
            if (item.s_equipment_maintenance_fee) water[index].equipment_maintenance_fee = 0;
            if (item.s_fire_cost) water[index].fire_cost = 0;
            if (item.s_late_payment) water[index].late_payment = 0;
            if (item.s_meter_connect_cost) water[index].meter_connect_cost = 0;
            if (item.s_other_income) water[index].other_income = 0;
            if (item.s_reduce_debt) water[index].reduce_debt = 0;
            if (item.s_waste_cost) water[index].waste_cost = 0;
            if (item.s_water_bill) water[index].water_bill = 0;
          }
        }
        for (let item of water) settleFlags(item, ["meter_connect_cost"]);
        this.setState({
          data: water
            .filter((e) => e.total_amount !== 0)
            .filter((e) => e.status_pay !== 3)
            .filter(
              (e) =>
                !this.state.status_transfer ||
                // NOTE เดิมเรียก e.status_transfer.toString() ตรง ๆ — บิลที่ไม่มีค่านี้ (null) ทำให้รายงานพังทั้งหน้า
                String(e.status_transfer ?? "") === this.state.status_transfer.value ||
                this.state.status_transfer.value === "0",
            ),
        });
      }
      loading(false);
    }
  };
  printReport13 = () => report13(this.state.data || [], this.state.end_date);
  // ส่งออก/อัปโหลดข้อมูลระบบ (P2)
  buildExportData = async (yyyymmEn) => {
    const [result1, result2, result3] = await Promise.all([
      GET(tokens, "v1/TH/report/overdue/download/" + getStorage("project_id"), {
        end_date: this.state.end_date ? format_date(this.state.end_date, "yyyy-mm", yyyymmEn) : "",
      }),
      GET(tokens, "v1/TH/project-manage/detail/" + getStorage("project_id"), null),
      GET(tokens, "v1/TH/customer-manage/filter-search/page/0/" + getStorage("project_id"), null),
    ]);
    const data = {
      data_water: result1.data.data_water,
      data_receipt: result1.data.data_receipt,
      transaction_payments: result1.data.transaction_payments.map((e) => ({
        ...e,
        project_id: result2.data.data.project_id,
      })),
      data_project: result2.data.data,
      data_customer: result3.data.data,
      data_setting: result1.data.data_setting,
      project_id: result2.data.data.project_id,
      // NOTE เดิมใช้ .toFixed(2) ของ JavaScript ตรง ๆ ซึ่งปัดค่า .xx5 ลง (ไม่ตรงกับ backend)
      //      และถ้า backend ส่งมาเป็นข้อความ .toFixed จะไม่มี ยอดกลายเป็น 0 เงียบ ๆ
      last_bookbank_balance: roundTo(Number(result1.data.last_bookbank_balance) || 0, 2),
      year: this.state.end_date
        ? Number(format_date(this.state.end_date, "yyyy", "en"))
        : new Date().getFullYear(),
      // NOTE เดิม fallback ใช้ new Date().getMonth() ซึ่งนับ 0-11 แต่ format_date "mm" นับ 1-12
      //      เวลาไม่ได้เลือกวันที่ เดือนที่ส่งออกจึงเพี้ยนไป 1 เดือน (มกราคมกลายเป็น 0)
      month: this.state.end_date
        ? Number(format_date(this.state.end_date, "mm", "en"))
        : new Date().getMonth() + 1,
    };
    return { result1, result2, data };
  };
  // progress 0→90 ระหว่างรอ API แล้วเร่ง →100
  startProgress = () => {
    this.setState({ loading_p2: true, percent: 0 });
    return setInterval(() => {
      this.setState((prev) => {
        let next = prev.percent + 1;
        if (next >= 90) next = 90;
        return { percent: next };
      });
    }, 300);
  };
  finishProgress = (progressTimer) => {
    clearInterval(progressTimer);
    let fastTimer = setInterval(() => {
      this.setState((prev) => {
        let next = prev.percent + 5;
        if (next >= 100) {
          clearInterval(fastTimer);
          loading(false);
          return { percent: 100, loading_p2: false };
        }
        return { percent: next };
      });
    }, 50);
  };
  exportToNewSite = async () => {
    if (!this.state.end_date) {
      alert("warning", "แจ้งเตือน", "กรุณาเลือกวันที่ส่งออกข้อมูล");
      return;
    }
    loading(true);
    let progressTimer = this.startProgress();
    const { result2, data } = await this.buildExportData("en");
    let text = JSON.stringify(data)
      .replaceAll(",", "%2C%")
      .replaceAll('"', "%22%")
      .replaceAll("{", "%7B%")
      .replaceAll("}", "%7D%")
      .replaceAll(":", "%3A%")
      .replaceAll("[", "%5B%")
      .replaceAll("]", "%5D%")
      .replaceAll("_", "%5F%");
    const blob = new Blob([text], { type: "text/plain" });
    const link = document.createElement("a");
    link.download = result2.data.data.project_name + ".txt";
    link.href = window.URL.createObjectURL(blob);
    link.dataset.downloadurl = ["text/plain", link.download, link.href].join(":");
    link.click();
    this.finishProgress(progressTimer);
  };
  uploadToNewSite = async () => {
    if (!this.state.end_date) {
      alert("warning", "แจ้งเตือน", "กรุณาเลือกวันที่ส่งออกข้อมูล");
      return;
    }
    loading(true);
    let progressTimer = this.startProgress();
    const { result1, data } = await this.buildExportData();
    let body = {
      ...data,
      data_other_income: {
        data: result1.data.data_setting.setting_document_pay.map((e) => ({
          title: [{ type: "text", value: e.title, required: false }],
          amount: 0,
          visible: true,
          title_editable: false,
          amount_editable: true,
        })),
        show: false,
      },
    };
    let result = await POST(
      tokens,
      process.env.REACT_APP_API_URLP2 + "api/public/upload-data/auto-p1",
      body,
    );
    if (result?.status) {
      alert("success", "สำเร็จ", "อัปโหลดข้อมูลสำเร็จ");
    }
    this.finishProgress(progressTimer);
  };
  clear_form() {
    loading(false);
    this.setState({
      page: 1,
      search: "",
      data: null,
      print_type: "",
      address_number: "",
      full_name: "",
      start_date: "",
      end_date: "",
      cut_off_date: "",
      title_report: { value: "0", label: "ทั้งหมด" },
      select_month: "",
      ref_cause: "0",
      payment_type: null,
      status_transfer: { value: "0", label: "ทั้งหมด" },
      month_count: "",
    });
  }
  render() {
    const {
      data,
      data_payment,
      loading: loadingState,
      loading_p2,
      percent,
      record_water_list,
      totals,
      report_type,
      select_month,
      data_customer,
      address_number,
      start_date,
      end_date,
      cut_off_date,
      payment_type,
      title_report,
      status_transfer,
      month_count,
    } = this.state;

    const cfg = REPORTS[report_type];
    const tableData = cfg && cfg.src === "record_water_list" ? record_water_list : data;
    const printBtn = PRINT_BUTTONS[report_type];

    const triggerPrint = (print_type) => {
      this.setState({ print_type });
      setTimeout(() => this.Print(), 1);
    };

    return (
      <div className="w-100 min-vh-100 fade-in">
        {loadingState && (
          <div className="spinner-container">
            <div className="loading-spinner"></div>
            {loading_p2 && (
              <div
                className="position-absolute"
                style={{
                  top: "calc(50% + 50px)",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                กำลังโหลดข้อมูล...{percent}%
              </div>
            )}
          </div>
        )}
        <Navbar page1="หน้าหลัก" page2="รายงานสรุปผล" />

        {/* BODY */}
        <div className="body">
          {perm.view && (
            <div className="p-3">
              <div className="row">
                <div className="col-12 col-md-4 col-lg-4 col-xl-3 col-xxl-3 mb-3">
                  {/* หัวข้อ และ ปุ่มเพิ่ม */}
                  <h4 className="mb-3">
                    <b>รายงานทั้งหมด</b>
                  </h4>
                  <div className="card border-0 p-3">
                    <div className="card-body p-0">
                      <table className="table table-borderless table-striped table-hover">
                        <tbody>
                          {REPORT_MENU.map((menu) => (
                            // รายงานที่เลือกอยู่ทำพื้นหลังฟ้าอ่อน ให้เห็นว่ากำลังดูอันไหน
                            // NOTE ตาราง bootstrap วาดสีแถบสลับ/hover ด้วย box-shadow ทับพื้นหลังของ tr
                            //      จึงต้องตั้งผ่านตัวแปร --bs-table-accent-bg แทนการใส่ background-color ตรง ๆ
                            <tr
                              key={menu.type}
                              className="pointer"
                              style={report_type === menu.type ? { "--bs-table-accent-bg": "var(--bs-blue-light)" } : undefined}
                              onClick={() => {
                                this.clear_form();
                                this.setState({ report_type: menu.type, ...(menu.extra || {}) });
                              }}
                            >
                              <td>{menu.type}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-8 col-lg-8 col-xl-9 col-xxl-9 pl-3">
                  {/* หัวข้อ และ ปุ่มเพิ่ม */}
                  <div className="d-flex row mx-0 align-items-center justify-content-between">
                    <h4 className="mb-3">
                      <b>{report_type || "รายงาน"}</b>
                    </h4>
                  </div>
                  <div className="card border-0 p-3">
                    {/* ค้นหาและตัวกรอง */}
                    {report_type && (
                      <div className="d-flex align-items-end mx-0 justify-content-between w-100 flex-wrap">
                        <div className="d-flex flex-wrap align-items-end">
                          {/* ค้นหา */}
                          {report_type !== "รายงานสรุปการจัดเก็บเงิน" &&
                            report_type !== "ส่งออกข้อมูลระบบ" && (
                              <div className="wpx-200 mx-1 mb-3 px-0">
                                <label className="w-100">บ้านเลขที่:</label>
                                <Select
                                  className="form-control bg-contain border-0 select-search w-100"
                                  styles={select_style}
                                  options={data_customer || []}
                                  onChange={(e) => {
                                    this.setState({ address_number: e });
                                  }}
                                  value={address_number}
                                  isClearable
                                ></Select>
                              </div>
                            )}
                          {/* วันที่เริ่มต้น */}
                          {![
                            "รายงานสรุปการจัดเก็บเงิน",
                            "รายงานลูกหนี้คงค้าง ณ เดือน",
                            "รายงานแจ้งหนี้ประจำเดือน",
                            "รายงานแจ้งหนี้คงค้างย้อนหลัง",
                            "รายงานใบเสร็จรับเงิน",
                            "รายงานอื่น ๆ ประจำเดือน",
                            "รายงานสถานะห้องชุด",
                            "ส่งออกข้อมูลระบบ",
                          ].includes(report_type) && (
                            <div className="wpx-200 mx-1 mb-3 px-0">
                              <label className="wpx-140">
                                {report_type === "รายงานการตัดหนี้"
                                  ? "วันที่จดมาตรเริ่มต้น"
                                  : "วันที่เริ่มต้น"}
                                :
                              </label>
                              <DatePicker
                                className="form-control bg-contain border-0 w-100"
                                placeholder="พิมพ์คำค้นหา..."
                                onChange={(e) => {
                                  this.setState({ start_date: e });
                                }}
                                value={start_date}
                              />
                            </div>
                          )}
                          {/* วันที่สิ้นสุด */}
                          {![
                            "รายงานสรุปการจัดเก็บเงิน",
                            "รายงานแจ้งหนี้ประจำเดือน",
                            "รายงานแจ้งหนี้คงค้างย้อนหลัง",
                            "รายงานใบเสร็จรับเงิน",
                            "รายงานอื่น ๆ ประจำเดือน",
                            "รายงานสถานะห้องชุด",
                            "ส่งออกข้อมูลระบบ",
                          ].includes(report_type) && (
                            <div className="wpx-200 mx-1 mb-3 px-0">
                              <label className="wpx-140">
                                {report_type === "รายงานการตัดหนี้"
                                  ? "วันที่จดมาตรสิ้นสุด"
                                  : "วันที่สิ้นสุด"}
                                :
                              </label>
                              <DatePicker
                                className="form-control bg-contain border-0 w-100"
                                placeholder="พิมพ์คำค้นหา..."
                                onChange={(e) => {
                                  this.setState({ end_date: e });
                                }}
                                value={end_date}
                              />
                            </div>
                          )}
                          {/* วันที่ตัดหนี้ */}
                          {report_type === "รายงานการตัดหนี้" && (
                            <div className="wpx-200 mx-1 mb-3 px-0">
                              <label className="wpx-140">วันที่ตัดหนี้:</label>
                              <DatePicker
                                className="form-control bg-contain border-0 w-100"
                                placeholder="พิมพ์คำค้นหา..."
                                onChange={(e) => {
                                  this.setState({ cut_off_date: e });
                                }}
                                value={cut_off_date}
                              />
                            </div>
                          )}
                          {/* เลือกเดือน */}
                          {[
                            "รายงานสรุปการจัดเก็บเงิน",
                            "รายงานแจ้งหนี้ประจำเดือน",
                            "รายงานสถานะห้องชุด",
                            "รายงานแจ้งหนี้คงค้างย้อนหลัง",
                            "รายงานใบเสร็จรับเงิน",
                            "รายงานอื่น ๆ ประจำเดือน",
                            "ส่งออกข้อมูลระบบ",
                          ].includes(report_type) && (
                            <div className="wpx-200 mx-1 mb-3 px-0">
                              <label className="wpx-140">เดือน/ปี:</label>
                              <input
                                type="month"
                                className="form-control bg-contain border-0 w-100"
                                placeholder="พิมพ์คำค้นหา..."
                                onChange={(e) => {
                                  this.setState({ select_month: e.target.value });
                                  if (e.target.value) {
                                    let date = new Date(e.target.value + "-01");
                                    let start_date = new Date(e.target.value + "-01");
                                    let end_date = new Date(date.setMonth(date.getMonth() + 1));
                                    end_date = new Date(end_date.setDate(end_date.getDate() - 1));
                                    this.setState({
                                      start_date: format_date(start_date, "yyyy-mm-dd", "en"),
                                      end_date: format_date(end_date, "yyyy-mm-dd", "en"),
                                    });
                                  } else {
                                    this.setState({ start_date: "", end_date: "" });
                                  }
                                }}
                                value={select_month}
                              />
                            </div>
                          )}
                          {/* ค้นหา */}
                          {report_type === "รายงานอื่น ๆ ประจำเดือน" && (
                            <div className="wpx-200 mx-1 mb-3 px-0">
                              <label className="w-100">รายการรับชำระ:</label>
                              <Select
                                className="form-control bg-contain border-0 select-search w-100"
                                styles={select_style}
                                options={data_payment || []}
                                onChange={(e) => {
                                  this.setState({ payment_type: e });
                                }}
                                value={payment_type}
                                isClearable
                              ></Select>
                            </div>
                          )}
                          {report_type === "รายงานลูกหนี้" && (
                            <div className="wpx-200 mx-1 mb-3 px-0">
                              <label className="w-100">หัวข้อรายงาน:</label>
                              <Select
                                className="form-control bg-contain border-0 select-search"
                                styles={select_style}
                                options={[
                                  { value: "0", label: "ทั้งหมด" },
                                  {
                                    value: "1",
                                    label: "ใช้น้ำน้อยกว่า 1 หน่วย หรือ ค้างชำระเกิน 1 ปี",
                                  },
                                  {
                                    value: "2",
                                    label:
                                      "ใช้น้ำเฉลี่ยตั้งแต่ 1 หน่วยขึ้นไป หรือ ค้างชำระไม่เกิน 1 ปี",
                                  },
                                ]}
                                onChange={(e) => {
                                  this.setState({ title_report: e });
                                }}
                                value={title_report}
                              ></Select>
                            </div>
                          )}
                          {[
                            "รายงานสถานะห้องชุด",
                            "รายงานหนี้ค้างชำระ",
                            "รายงานลูกหนี้-โครงการ",
                            "รายงานการตัดหนี้",
                            "รายงานแจ้งหนี้ประจำเดือน",
                            "รายงานแจ้งหนี้คงค้างย้อนหลัง",
                            "รายงานลูกหนี้คงค้าง ณ เดือน",
                          ].includes(report_type) && (
                            <div className="wpx-200 mx-1 mb-3 px-0">
                              <label className="w-100">รายการสถานะการโอน:</label>
                              <Select
                                className="form-control bg-contain border-0 select-search w-100"
                                styles={select_style}
                                options={[
                                  { value: "0", label: "ทั้งหมด" },
                                  { value: "1", label: "ลูกค้าที่โอนกรรมสิทธิ์" },
                                  { value: "2", label: "ลูกค้าที่ยังไม่โอน" },
                                  { value: "3", label: "ห้องว่าง" },
                                ]}
                                onChange={(e) => {
                                  this.setState({ status_transfer: e });
                                }}
                                value={status_transfer}
                                isClearable
                              ></Select>
                            </div>
                          )}
                          {/* เลือกเดือน */}
                          {report_type === "รายงานลูกหนี้" && (
                            <div className="wpx-200 mx-1 mb-3 px-0">
                              <label className="wpx-140">จำนวนเดือน:</label>
                              <input
                                type="number"
                                className="form-control bg-contain border-0 w-100"
                                placeholder="จำนวนเดือน..."
                                onChange={(e) => {
                                  this.setState({ month_count: e.target.value });
                                }}
                                value={month_count}
                              />
                            </div>
                          )}
                          {report_type !== "ส่งออกข้อมูลระบบ" && (
                            <button
                              className="btn btn-primary px-3 mb-3 mx-1"
                              onClick={() => this.runReport(report_type)}
                            >
                              ค้นหา
                            </button>
                          )}
                          {report_type !== "ส่งออกข้อมูลระบบ" && (
                            <button
                              className="btn btn-outline-primary px-3 mb-3 mx-1"
                              onClick={() => {
                                this.clear_form();
                                this.runReport(report_type);
                              }}
                            >
                              รีเซ็ท
                            </button>
                          )}
                        </div>
                        <div className="d-flex ml-auto">
                          {report_type === "ส่งออกข้อมูลระบบ" && (
                            <div className="mx-0 px-0 d-flex">
                              <button
                                className="btn btn-primary px-3 mb-3 mx-1"
                                onClick={() => {
                                  this.exportToNewSite();
                                }}
                              >
                                ส่งออกข้อมูลระบบ
                              </button>
                              <button
                                className="btn btn-success px-3 mb-3 mx-1"
                                onClick={() => {
                                  this.uploadToNewSite();
                                }}
                              >
                                อัปโหลดข้อมูลอัตโนมัติ
                              </button>
                            </div>
                          )}
                          {report_type === "รายงานสถานะห้องชุด" && (
                            <div className="mx-0 px-0">
                              <button
                                className="btn btn-outline-primary mx-1 mb-3"
                                onClick={() => triggerPrint("พิมพ์สถานะห้องชุด")}
                              >
                                พิมพ์สถานะห้องชุด
                              </button>
                            </div>
                          )}
                        </div>
                        {data && printBtn && (
                          <div className="mx-0 px-0">
                            <button
                              className="btn btn-outline-primary mx-1 mb-3"
                              onClick={() => triggerPrint(printBtn.print)}
                            >
                              {printBtn.label}
                            </button>
                          </div>
                        )}
                        {data && (
                          <div className="mx-0 px-0">
                            <DownloadTableExcel
                              filename={report_type}
                              sheet={report_type}
                              currentTableRef={this.tableRef.current}
                            >
                              <button className="btn btn-outline-primary mx-1 mb-3">
                                ส่งออก excel
                              </button>
                            </DownloadTableExcel>
                          </div>
                        )}
                      </div>
                    )}
                    {cfg && cfg.markDup && tableData && (() => {
                      const dupRows = findDupIndexes(tableData).size;
                      return dupRows > 0 ? (
                        <div className="small mb-2" style={{ color: "#b42318" }}>
                          พบบิลซ้ำเกินมา {dupRows} รายการ (ไฮไลต์สีแดงอ่อนในตาราง)
                        </div>
                      ) : null;
                    })()}
                    {/* ตาราง */}
                    <div className="table-responsive">
                      <table
                        className="table table-borderless table-striped"
                        ref={this.tableRef}
                      >
                        {cfg && tableData && (
                          <ReportTable cfg={cfg} data={tableData} totals={totals} />
                        )}
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* ปุ่มลอยกลับขึ้นบน (คอมโพเนนต์กลาง จัดการ scroll เองในตัว) */}
          <ScrollTopButton />
        </div>
      </div>
    );
  }
}
